// Screen 1 of the teams feature: the teams this person manages. See kumpula/TEAMS.md.
//
// A team manager is an ordinary customer, not an admin - they never see /admin/*, which is the
// entire point of the feature. Everything on this screen comes from one teams-managerteams call;
// the client has no Firestore access to any teams collection.
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Alert, Badge } from 'reactstrap'
import moment from 'moment'
import withAppconfig from '../../AppContext'
import TopNavigation from '../../TopNavigation'
import LottieLoading from '../../components/LottieLoading'
import { useManagerTeams } from '../../utils/useManagerTeams'
import { describeFormat } from '../../utils/teamformats'
import {
  choiceGuidance,
  formatDateRange,
  openSeasonTies,
  seasonWindow,
  spareCountdown,
} from '../../utils/teamsview'

// Shown on every teams screen: the backend's refusal messages are already Finnish sentences
// meant for the user, so they are displayed verbatim. Only a transport failure needs our own
// wording.
export function TeamsFailure({ failure }) {
  const { t } = useTranslation()
  if (!failure) return null
  return (
    <Alert color='warning'>{failure.message || t('teams_network_error')}</Alert>
  )
}

function QuotaLine({ quota }) {
  const { t } = useTranslation()
  if (!quota) return null
  return (
    <div>
      <strong>{t('teams_quota')}:</strong> {quota.used} / {quota.quota}
      {quota.pending ? (
        <span className='text-muted'>
          {' '}
          ({quota.confirmed} {t('teams_confirmed')}, {quota.pending} {t('teams_pending')})
        </span>
      ) : null}
    </div>
  )
}

/**
 * The two-choice rule, as a to-do or a warning depending on where the manager is.
 *
 * The +1 in the quota is a league rule, not slack: every opponent must be offered at least two
 * dates. A team that has not started is short by definition, so that case reads as a to-do that
 * says how many dates to book and why - which is also the only place the UI states the +1 in
 * words. It only becomes a warning once opponents have begun confirming, because then a
 * shortfall means somebody is about to be handed a single date.
 */
export function ChoiceGuidance({ team, quota }) {
  const { t } = useTranslation()
  const guidance = choiceGuidance(team, quota)
  if (!guidance) return null

  const fill = (key) =>
    t(key)
      .replace('{NEEDED}', guidance.needed)
      .replace('{SHORTFALL}', guidance.shortfall)
      .replace('{UNCLAIMED}', guidance.unclaimed)
      .replace('{REMAINING}', guidance.remaining)
      .replace('{HOMEMATCHES}', guidance.homematches)

  const message = {
    start: 'teams_todo_start',
    more: 'teams_todo_more',
    short: 'teams_choices_warning',
    noquota: 'teams_choices_noquota',
  }[guidance.kind]

  return (
    <Alert color={guidance.tone === 'warning' ? 'warning' : 'info'} className='mt-2 mb-0'>
      {fill(message)}
      {/* Say why the extra date exists, but only while it is still advice rather than a fault. */}
      {guidance.tone === 'todo' ? (
        <div className='small mt-1'>{t('teams_todo_why')}</div>
      ) : null}
    </Alert>
  )
}

// The spare is whichever date nobody clicked. Once the confirmed count reaches homematches the
// manager has 24 h to release it themselves, after which the backend does - and its courts
// disappear from the season view, so the countdown has to be visible.
export function SpareNotice({ quota }) {
  const { t } = useTranslation()
  const countdown = spareCountdown(quota && quota.spare)
  if (!countdown) return null
  return (
    <Alert color={countdown.overdue ? 'danger' : 'info'} className='mt-2 mb-0'>
      {countdown.overdue
        ? t('teams_spare_overdue')
        : t('teams_spare_countdown').replace('{HOURS}', countdown.hours)}
    </Alert>
  )
}

function TeamCard({ team, season, format, ties, quota, onOpen, onNewTie }) {
  const { t } = useTranslation()
  const today = moment().format('YYYY-MM-DD')
  const window = seasonWindow(season, today)
  const open = openSeasonTies(ties, team)

  return (
    <Card className='mb-3'>
      <CardHeader>
        <strong>{team.name}</strong>
        {team.state === 'paused' ? (
          <Badge color='secondary' className='ml-2'>
            {t('teams_paused')}
          </Badge>
        ) : null}
      </CardHeader>
      <CardBody>
        {season ? (
          <div className='text-muted'>
            <div>{season.name}</div>
            {formatDateRange(season.validstart, season.validend) ? (
              <div className='small'>
                {t('teams_season_active')} {formatDateRange(season.validstart, season.validend)}
              </div>
            ) : null}
          </div>
        ) : null}
        <div className='mt-2'>{describeFormat(format)}</div>
        <div className='mt-2'>
          <QuotaLine quota={quota} />
          <div className='text-muted'>
            {open.length} {t('teams_ties_open')}
          </div>
        </div>

        <ChoiceGuidance team={team} quota={quota} />
        <SpareNotice quota={quota} />

        {/* Say what the season window allows rather than letting the manager find out from a
            rejected call. Cancelling still works after the window shuts. */}
        {window.state === 'not-open' ? (
          <Alert color='secondary' className='mt-2 mb-0'>
            {t('teams_booking_not_open').replace('{DATE}', window.opens || '')}
          </Alert>
        ) : null}
        {window.state === 'closed' ? (
          <Alert color='secondary' className='mt-2 mb-0'>
            {t('teams_booking_closed')}
          </Alert>
        ) : null}

        <div className='mt-3'>
          <Button color='primary' onClick={() => onOpen(team)}>
            {t('teams_open_season')}
          </Button>
          {window.canBook && team.state === 'active' ? (
            <Button color='secondary' className='ml-2' onClick={() => onNewTie(team)}>
              {t('teams_new_tie')}
            </Button>
          ) : null}
        </div>
      </CardBody>
    </Card>
  )
}

function TeamsScreen({ appconfig }) {
  const { t } = useTranslation()
  const history = useHistory()
  const { loading, data, failure } = useManagerTeams({ appconfig })

  const go = (path) => history.push(appconfig.pathPrefix + path)

  const teams = (data && data.teams) || []
  const seasons = (data && data.seasons) || []
  const formats = (data && data.formats) || []

  return (
    <div className='full-page-background'>
      <TopNavigation />
      <Container>
        <Row>
          <Col xs='12'>
            <h4 className='mt-3 mb-3'>{t('teams_title')}</h4>
          </Col>
        </Row>
        <Row>
          <Col xs='12' md='8'>
            {loading ? (
              <LottieLoading width={150} height={150} />
            ) : (
              <>
                <TeamsFailure failure={failure} />
                {!failure && teams.length === 0 ? (
                  <Card>
                    <CardBody>{t('teams_none')}</CardBody>
                  </Card>
                ) : null}
                {teams.map((team) => (
                  <TeamCard
                    key={team.id}
                    team={team}
                    season={seasons.find((s) => s.id === team.seasonid) || null}
                    format={formats.find((f) => f.id === team.formatid) || null}
                    ties={data.ties}
                    quota={(data.quota || {})[team.id] || null}
                    onOpen={(x) => go('/teams/' + x.id + '/season')}
                    onNewTie={(x) => go('/teams/' + x.id + '/tie')}
                  />
                ))}
              </>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default withAppconfig(TeamsScreen)
